import { useEffect, useState } from 'react';

/**
 * Hook to access dynamic branding colors from CSS custom properties
 * These are set by the DataContext when organization branding is loaded
 */
export const useBrandingColors = () => {
  const [colors, setColors] = useState({
    primary: '#4f46e5', // Default indigo
    secondary: '#7c3aed', // Default purple
  });

  useEffect(() => {
    // Get colors from CSS custom properties
    const updateColors = () => {
      const root = document.documentElement;
      const primary = getComputedStyle(root).getPropertyValue('--primary-color').trim() || '#4f46e5';
      const secondary = getComputedStyle(root).getPropertyValue('--secondary-color').trim() || '#7c3aed';
      
      setColors({ primary, secondary });
    };

    // Initial load
    updateColors();

    // Watch for changes (when branding is updated)
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, []);

  return colors;
};

/**
 * Helper function to get lighter/darker shades of a color
 */
export const adjustColor = (color, amount) => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  const newR = Math.max(0, Math.min(255, r + amount));
  const newG = Math.max(0, Math.min(255, g + amount));
  const newB = Math.max(0, Math.min(255, b + amount));

  // Convert back to hex
  return `#${((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)}`;
};

/**
 * Generate Tailwind-compatible color utilities
 */
export const getBrandingStyles = (colors) => {
  return {
    primaryBg: { backgroundColor: colors.primary },
    primaryText: { color: colors.primary },
    primaryBorder: { borderColor: colors.primary },
    primaryHoverBg: { backgroundColor: adjustColor(colors.primary, -20) },
    
    secondaryBg: { backgroundColor: colors.secondary },
    secondaryText: { color: colors.secondary },
    secondaryBorder: { borderColor: colors.secondary },
    secondaryHoverBg: { backgroundColor: adjustColor(colors.secondary, -20) },
  };
};
